import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore, Gavel, Security, Cookie, Policy } from '@mui/icons-material';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function LegalPage() {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Legal & Privacy
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Last updated: February 2025
      </Typography>

      <Paper elevation={2} sx={{ mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Gavel />} label="Terms of Service" iconPosition="start" />
          <Tab icon={<Security />} label="Privacy Policy" iconPosition="start" />
          <Tab icon={<Cookie />} label="Cookie Policy" iconPosition="start" />
          <Tab icon={<Policy />} label="Acceptable Use" iconPosition="start" />
        </Tabs>

        {/* Terms of Service */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h5" gutterBottom>Terms of Service</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Effective Date: February 1, 2025
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">1. Acceptance of Terms</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  By accessing or using the EIS Platform ("Service"), you agree to be bound by these Terms of Service.
                  If you do not agree to these terms, you may not use the Service.
                </Typography>
                <Typography variant="body1">
                  The Service is provided by Evolunic ("Company", "we", "us", or "our").
                  These terms apply to all users of the Service, including without limitation users who are contributors,
                  browsers, vendors, customers, merchants, and/or contributors of content.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">2. Description of Service</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  EIS Platform is an engineering information system that provides:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Project and document management" /></ListItem>
                  <ListItem><ListItemText primary="CAD file viewing and analysis" /></ListItem>
                  <ListItem><ListItemText primary="AI-assisted technical document generation" /></ListItem>
                  <ListItem><ListItemText primary="Bill of Materials (BOM) management" /></ListItem>
                  <ListItem><ListItemText primary="Collaboration and version control features" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">3. User Accounts</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  To use certain features of the Service, you must register for an account.
                  You are responsible for maintaining the confidentiality of your account credentials
                  and for all activities that occur under your account.
                </Typography>
                <Typography variant="body1">
                  You agree to: (a) provide accurate and complete registration information;
                  (b) maintain and promptly update your account information;
                  (c) maintain the security of your password and accept all risks of unauthorized access;
                  (d) promptly notify us if you discover or suspect any security breaches.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">4. Intellectual Property</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  <strong>Your Content:</strong> You retain ownership of any intellectual property rights
                  that you hold in the content you create, upload, or store on the Service ("Your Content").
                  By uploading Your Content, you grant us a limited license to store, process, and display
                  Your Content solely for the purpose of providing the Service to you.
                </Typography>
                <Typography variant="body1">
                  <strong>Our Content:</strong> The Service and its original content (excluding Your Content),
                  features, and functionality are and will remain the exclusive property of Evolunic.
                  The Service is protected by copyright, trademark, and other laws.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">5. Data Processing & Security</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  We implement appropriate technical and organizational measures to protect your data.
                  Your Content is stored on secure AWS infrastructure with encryption at rest and in transit.
                </Typography>
                <Typography variant="body1">
                  For enterprise customers, we offer Data Processing Agreements (DPA) that comply with
                  GDPR and other applicable data protection regulations. Contact us for more information.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">6. Limitation of Liability</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  To the maximum extent permitted by law, Evolunic shall not be liable for any indirect,
                  incidental, special, consequential, or punitive damages, or any loss of profits or revenues,
                  whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
                </Typography>
                <Typography variant="body1">
                  In no event shall our aggregate liability exceed the amount you paid us, if any,
                  in the past twelve months for the Service.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">7. Termination</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  We may terminate or suspend your account and access to the Service immediately,
                  without prior notice or liability, for any reason, including without limitation
                  if you breach these Terms.
                </Typography>
                <Typography variant="body1">
                  Upon termination, your right to use the Service will immediately cease.
                  You may export your data before termination using our data export feature.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">8. Contact Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  If you have any questions about these Terms, please contact us at:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Email: legal@evolunic.com" /></ListItem>
                  <ListItem><ListItemText primary="Address: [Company Address]" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>

        {/* Privacy Policy */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h5" gutterBottom>Privacy Policy</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Effective Date: February 1, 2025
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">1. Information We Collect</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  <strong>Account Information:</strong> When you create an account, we collect your name,
                  email address, and password. If you sign up using OAuth (Google, GitHub),
                  we receive your name, email, and profile picture from the provider.
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Content You Provide:</strong> We store the projects, documents, CAD files,
                  and other content you upload to the Service.
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Usage Information:</strong> We collect information about how you use the Service,
                  including actions you take, features you use, and time spent.
                </Typography>
                <Typography variant="body1">
                  <strong>Device & Log Information:</strong> We collect device type, browser type,
                  IP address, and access times for security and troubleshooting purposes.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">2. How We Use Your Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>We use your information to:</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Provide, maintain, and improve the Service" /></ListItem>
                  <ListItem><ListItemText primary="Process transactions and send related information" /></ListItem>
                  <ListItem><ListItemText primary="Send technical notices, updates, and security alerts" /></ListItem>
                  <ListItem><ListItemText primary="Respond to your comments, questions, and support requests" /></ListItem>
                  <ListItem><ListItemText primary="Monitor and analyze trends, usage, and activities" /></ListItem>
                  <ListItem><ListItemText primary="Detect, investigate, and prevent fraudulent or illegal activities" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">3. Data Sharing</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  We do not sell your personal information. We may share your information with:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Service providers who assist in operating our Service (AWS, analytics)" /></ListItem>
                  <ListItem><ListItemText primary="Other users you choose to collaborate with on projects" /></ListItem>
                  <ListItem><ListItemText primary="Law enforcement when required by law or to protect rights" /></ListItem>
                  <ListItem><ListItemText primary="Acquirers in case of merger, acquisition, or sale of assets" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">4. Data Retention</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  We retain your information for as long as your account is active or as needed to provide services.
                  When you delete your account, we retain your data for 30 days (grace period) before permanent deletion.
                </Typography>
                <Typography variant="body1">
                  Specific retention periods:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Account data: Until account deletion + 30 days" /></ListItem>
                  <ListItem><ListItemText primary="Activity logs: 2 years" /></ListItem>
                  <ListItem><ListItemText primary="Authentication logs: 90 days" /></ListItem>
                  <ListItem><ListItemText primary="Support communications: 3 years" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">5. Your Rights (GDPR)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  If you are in the European Economic Area (EEA), you have the following rights:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Right to Access: Request a copy of your personal data" /></ListItem>
                  <ListItem><ListItemText primary="Right to Rectification: Request correction of inaccurate data" /></ListItem>
                  <ListItem><ListItemText primary="Right to Erasure: Request deletion of your data" /></ListItem>
                  <ListItem><ListItemText primary="Right to Portability: Receive your data in a portable format" /></ListItem>
                  <ListItem><ListItemText primary="Right to Object: Object to processing of your data" /></ListItem>
                  <ListItem><ListItemText primary="Right to Withdraw Consent: Withdraw consent at any time" /></ListItem>
                </List>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  To exercise these rights, go to Settings &gt; Privacy or contact privacy@evolunic.com.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">6. Data Security</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  We implement industry-standard security measures including:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Encryption in transit (TLS 1.2+) and at rest (AES-256)" /></ListItem>
                  <ListItem><ListItemText primary="Regular security audits and penetration testing" /></ListItem>
                  <ListItem><ListItemText primary="Multi-factor authentication option" /></ListItem>
                  <ListItem><ListItemText primary="Access controls and audit logging" /></ListItem>
                  <ListItem><ListItemText primary="AWS WAF and DDoS protection" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">7. Contact Us</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  For privacy-related inquiries:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Data Protection Officer: dpo@evolunic.com" /></ListItem>
                  <ListItem><ListItemText primary="Privacy inquiries: privacy@evolunic.com" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>

        {/* Cookie Policy */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h5" gutterBottom>Cookie Policy</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Effective Date: February 1, 2025
            </Typography>

            <Typography variant="body1" paragraph>
              This Cookie Policy explains how EIS Platform uses cookies and similar technologies
              to recognize you when you visit our website.
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">What Are Cookies?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  Cookies are small data files placed on your device when you visit a website.
                  Cookies are widely used to make websites work efficiently and provide information to site owners.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Types of Cookies We Use</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  <strong>Essential Cookies (Required):</strong> These cookies are necessary for the website to function
                  and cannot be switched off. They are usually set in response to actions you take,
                  such as setting privacy preferences, logging in, or filling forms.
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Performance Cookies (Optional):</strong> These cookies allow us to count visits and traffic sources
                  to measure and improve site performance. All information collected is aggregated and anonymous.
                </Typography>
                <Typography variant="body1">
                  <strong>Functional Cookies (Optional):</strong> These cookies enable enhanced functionality
                  and personalization, such as remembering your preferences.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Specific Cookies We Use</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="eis_session_id"
                      secondary="Essential - Maintains your session state"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="eis_auth_token"
                      secondary="Essential - Authentication token (stored securely)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="eis_theme"
                      secondary="Functional - Remembers your theme preference (light/dark)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="eis_language"
                      secondary="Functional - Remembers your language preference"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="eis_cookie_consent"
                      secondary="Essential - Records your cookie preferences"
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Managing Cookies</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  You can set your browser to refuse all or some cookies, or to alert you when cookies are being sent.
                  However, if you disable or refuse essential cookies, some parts of the website may not function properly.
                </Typography>
                <Typography variant="body1">
                  You can manage your cookie preferences in Settings &gt; Privacy &gt; Cookie Preferences.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>

        {/* Acceptable Use Policy */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h5" gutterBottom>Acceptable Use Policy</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Effective Date: February 1, 2025
            </Typography>

            <Typography variant="body1" paragraph>
              This Acceptable Use Policy sets out the terms between you and us under which you may access
              and use EIS Platform. By using the Service, you agree to comply with this policy.
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Prohibited Uses</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>You must not use the Service:</Typography>
                <List dense>
                  <ListItem><ListItemText primary="To upload, store, or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable" /></ListItem>
                  <ListItem><ListItemText primary="To impersonate any person or entity, or falsely state or misrepresent your affiliation with a person or entity" /></ListItem>
                  <ListItem><ListItemText primary="To upload, store, or transmit any content that infringes any patent, trademark, trade secret, copyright, or other intellectual property rights" /></ListItem>
                  <ListItem><ListItemText primary="To upload, store, or transmit any material that contains viruses, Trojan horses, worms, or any other harmful programs" /></ListItem>
                  <ListItem><ListItemText primary="To attempt to gain unauthorized access to the Service, other accounts, computer systems, or networks" /></ListItem>
                  <ListItem><ListItemText primary="To interfere with or disrupt the Service or servers or networks connected to the Service" /></ListItem>
                  <ListItem><ListItemText primary="To use the Service for any illegal purpose or in violation of any applicable laws or regulations" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Content Guidelines</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  You are responsible for all content you upload to the Service. Content must:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Be accurate and not misleading" /></ListItem>
                  <ListItem><ListItemText primary="Comply with applicable laws in all relevant jurisdictions" /></ListItem>
                  <ListItem><ListItemText primary="Not contain any confidential information you are not authorized to share" /></ListItem>
                  <ListItem><ListItemText primary="Not infringe any third-party rights" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Security Requirements</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>To maintain the security of the Service, you must:</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Keep your login credentials confidential and not share them with others" /></ListItem>
                  <ListItem><ListItemText primary="Use a strong, unique password for your account" /></ListItem>
                  <ListItem><ListItemText primary="Enable two-factor authentication if available" /></ListItem>
                  <ListItem><ListItemText primary="Notify us immediately of any unauthorized use of your account" /></ListItem>
                  <ListItem><ListItemText primary="Not attempt to bypass any security measures or access restrictions" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Enforcement</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  We reserve the right to investigate and take appropriate action against anyone who,
                  in our sole discretion, violates this policy, including without limitation:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Removing or disabling access to content that violates this policy" /></ListItem>
                  <ListItem><ListItemText primary="Suspending or terminating the account of violators" /></ListItem>
                  <ListItem><ListItemText primary="Reporting violators to law enforcement authorities" /></ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Reporting Violations</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  If you become aware of any violation of this Acceptable Use Policy,
                  please report it to: abuse@evolunic.com
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}
